import { useState, useRef, useCallback } from 'react'
import { log } from '../lib/log';
import { LibraryConfig } from '../contexts/config.context';

export type ActiveChatMode = 'chat' | 'call' | null;
interface WebSocketCallbacks {
  onOpen?: (event: Event, mode: ActiveChatMode) => void;
  onMessage?: (event: MessageEvent, mode: ActiveChatMode) => void;
  onError?: (event: Event, mode: ActiveChatMode) => void;
  onClose?: (event: CloseEvent, mode: ActiveChatMode) => void;
}

interface WebSocketOptions extends WebSocketCallbacks {
  url: string;
  config: LibraryConfig,
  retryInterval?: number; // milliseconds
  language?: string;
}

function cleanLanguage(inp: string): string {
  let idx = inp.indexOf('(');
  let sub = inp;

  if (idx > -1) {
    sub = inp.substring(0, idx);
  }
  sub = sub.toLowerCase().trim().replace(/\s+/, '_');
  return sub;
}

export function useWebsocket(options: WebSocketOptions) {
  const [isConnected, setIsConnected] = useState(false);

  const ws = useRef<WebSocket | null>(null);
  const retryTimeout = useRef<NodeJS.Timeout | null>(null);
  const modeRef = useRef<{ mode: ActiveChatMode }>({ mode: null });

  const connect = (session_id?: string | null) => {
    if (retryTimeout.current) {
      clearTimeout(retryTimeout.current)
      retryTimeout.current = null;
    }

    const connUrl = new URL(options.url)
    if (session_id) {
      connUrl.searchParams.set("sid", session_id)
    }
    if (options.language) {
      const cleanedLanguage = cleanLanguage(options.language)
      connUrl.searchParams.set("lang", cleanedLanguage)
    }

    ws.current = new WebSocket(connUrl);
    log('info', 'WebSocket Attempting to connect...');

    ws.current.onopen = (event) => {
      log('info', "WebSocket Connected");
      setIsConnected(true);
      options.onOpen?.(event, modeRef.current.mode)
    }

    ws.current.onmessage = (event) => {
      options.onMessage?.(event, modeRef.current.mode)
    }

    ws.current.onerror = (event) => {
      log('info', "WebSocket Error: ", event);
      options.onError?.(event, modeRef.current.mode)
    }

    ws.current.onclose = (event) => {
      log('info', `WebSocket Disconnected. Code: ${event.code}, Reason: ${event.reason}`);
      setIsConnected(false);
      options.onClose?.(event, modeRef.current.mode);

      if (event.code !== 1000 && options.retryInterval && options.retryInterval > 0) {
        log('info', `WebSocket Attempting reconnect in ${options.retryInterval}ms...`);
        retryTimeout.current = setTimeout(connect, options.retryInterval);
      }
    }
  }

  const disconnect = useCallback(() => {
    if (retryTimeout.current) {
      clearTimeout(retryTimeout.current);
      retryTimeout.current = null;
    }
    if (ws.current) {
      log('info', 'WebSocket Closing connection manually.');
      ws.current.close(1000, "Manual disconnect"); // Use code 1000 for clean close
      ws.current = null;
      setIsConnected(false);
    }
  }, []);

  const sendMessage = useCallback((message: object) => {
    if (ws.current && ws.current.readyState === WebSocket.OPEN) {
      ws.current.send(JSON.stringify({ event: "message", data: message }));
    } else {
      log('error', 'WebSocket Cannot send message: connection not open.');
    }
  }, [])

  const sendAudio = useCallback((blob: Blob) => {
    if (ws.current && ws.current.readyState === WebSocket.OPEN) {
      const bfr_promise = new Promise<string>((res) => {
        const reader = new FileReader();

        reader.onload = () => {
          if (typeof reader.result === 'string') {
            const base64Audio = reader.result.split(',')[1]; // Extract base64 part
            return res(base64Audio)
          }
        };

        reader.readAsDataURL(blob);
      })

      bfr_promise.then((bfr) => {
        ws.current?.send(JSON.stringify({ event: 'audio', data: bfr }));
      })
    }
    else {
      log('error', 'WebSocket Cannot send message: connection not open.');
    }
  }, [])

  return {
    isConnected,
    connect,
    disconnect,
    sendMessage,
    sendAudio,
    setActiveMode(value: ActiveChatMode) {
      modeRef.current.mode = value
    },
  }
}
